// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { ProjectModel } from '@project/model/project.model';
import { ArticleNumberMappingListVO, ArticleNumberMappingVO } from '../../../model/vo/article-number-mapping.vo';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleCollectionVO, ArticleVO } from '../../../model/vo/article.vo';
import { ArticleManager } from '../../../manager/article.manager';
import { UserProfileUtils } from '@user/service/user-profile-utils';
import { UserModel } from '@user/model/user.model';
import { ArrayUtils } from '@shared/util/array-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ArticleDataService } from '../../../service/article-data.service';
import { FileUtils } from '@shared/util/file-utils';
import { forkJoin, lastValueFrom, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CSVUtils } from './csv-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { CollectionService } from '@user/components/favorites/service/collection.service';
import { UserComponentPresenter } from '@user/components/user-component.presenter';
import { ArticleNumberMappingService } from '../../../service/article-number-mapping.service';
import { UserModelService } from '@user/model/user.model.service';
import { ArticleComponentsPresenter } from '@admintool/components/presenter/article-components.presenter';

/**
 * Overview of all article number mapping lists
 */
@Component({
  standalone: false,
  selector: 'app-edit-article-number-mapping',
  templateUrl: './edit-article-number-mapping.component.html',
})
export class EditArticleNumberMappingComponent implements OnInit {
  public readonly ARTICLE_NUMBER_MAPPING_LIST_DEFAULT_NAME: string = 'items.label.default_custom_article_number_list';

  public articleNumberMappingLists: ArticleNumberMappingListVO[];
  public activeArticleNumberMappingList: ArticleNumberMappingListVO;
  public selectedArticleNumberMapping: ArticleNumberMappingVO;

  @Input()
  public editedCollection: ArticleCollectionVO;

  public update = new EventEmitter<void>();

  public addToNotFavoriteAvailable: boolean;
  public addToEditedCollectionAvailable: boolean;

  private readonly VALUES_SEPARATOR: string = ';';
  private readonly FILE_NAME: string = 'ArticleNumbers';
  private readonly LINES_SEPARATOR: string = '\n';
  private readonly MAX_ARTICLE_NUMBER_LENGTH = 200;

  private _requestedFavoriteArticleNumbers: string[];

  constructor(
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    private alertHelper: AlertHelper,
    private articleDataService: ArticleDataService,
    private projectModel: ProjectModel,
    private articleManager: ArticleManager,
    private localeResourceManager: LocaleResourceManager,
    private collectionService: CollectionService,
    private _userComponentPresenter: UserComponentPresenter,
    private articleNumberMappingService: ArticleNumberMappingService,
    private userModelService: UserModelService,
    private articleComponentsPresenter: ArticleComponentsPresenter,
  ) {
    this.addToNotFavoriteAvailable = !!this.getNotFavoriteArticleCollections().length;
    this.postInit();
  }

  public postInit(): void {
    this.articleNumberMappingLists = this.cloneArticleNumberMappingLists(this.userModel.articleNumberMappingLists);
    this.activeArticleNumberMappingList = this.articleNumberMappingLists.find((list: ArticleNumberMappingListVO) => list.isActive);
    this.sortArticleNumberMappings(this.activeArticleNumberMappingList.entries);
  }

  public ngOnInit(): void {
    if (this.editedCollection && this.editedCollection.type !== ArticleCollectionVO.TYPE_FAVORITES) {
      this.addToEditedCollectionAvailable = true;
    }
  }

  private sortArticleNumberMappings(mappings: ArticleNumberMappingVO[]): void {
    if (ArrayUtils.isEmpty(mappings)) {
      return;
    }
    mappings.sort((a: ArticleNumberMappingVO, b: ArticleNumberMappingVO) => {
      return StringUtils.stringCompare(a.articleNumber, b.articleNumber);
    });
  }

  public onActiveMappingListChanged(): void {
    this.articleNumberMappingLists.forEach((list: ArticleNumberMappingListVO) => (list.isActive = false));
    this.activeArticleNumberMappingList.isActive = true;
    this.sortArticleNumberMappings(this.activeArticleNumberMappingList.entries);
    this.update.emit();
  }

  public addNewMappingList(): void {
    this.articleComponentsPresenter.openAddCustomArticleNumberListDialog().then((newList) => {
      if (newList && newList instanceof ArticleNumberMappingListVO) {
        this.articleNumberMappingLists.forEach((list: ArticleNumberMappingListVO) => (list.isActive = false));
        this.articleNumberMappingLists.push(newList);
        this.activeArticleNumberMappingList = newList;
        this.update.emit();
      }
    });
  }

  public removeMappingList(): void {
    if (this.activeArticleNumberMappingList.isDefault) {
      return;
    }
    const index: number = this.articleNumberMappingLists.findIndex(
      (list: ArticleNumberMappingListVO) => list === this.activeArticleNumberMappingList
    );
    this.articleNumberMappingLists.splice(index, 1);
    this.activeArticleNumberMappingList = this.articleNumberMappingLists.find((list: ArticleNumberMappingListVO) => list.isDefault);
    this.activeArticleNumberMappingList.isActive = true;
    this.sortArticleNumberMappings(this.activeArticleNumberMappingList.entries);
    this.update.emit();
  }

  public toggleMapping(articleNumberMapping: ArticleNumberMappingVO): void {
    if (this.selectedArticleNumberMapping && this.selectedArticleNumberMapping === articleNumberMapping) {
      this.selectedArticleNumberMapping = null;
    } else {
      this.selectedArticleNumberMapping = articleNumberMapping;
    }
  }

  public selectMapping(articleNumberMapping: ArticleNumberMappingVO): void {
    this.selectedArticleNumberMapping = articleNumberMapping;
  }

  public addArticleNumberMapping(): void {
    this.articleComponentsPresenter.openAddCustomArticleNumberDialog(this.activeArticleNumberMappingList.entries).then((data) => {
      if (data && data instanceof ArticleNumberMappingVO) {
        this.activeArticleNumberMappingList.entries.push(data);
        this.update.emit();
      }
    });
  }

  public editArticleNumberMapping(): void {
    if (!this.selectedArticleNumberMapping) {
      return;
    }
    this.articleComponentsPresenter.openAddCustomArticleNumberDialog(this.activeArticleNumberMappingList.entries, this.selectedArticleNumberMapping);
    this.update.emit();
  }

  public deleteArticleNumberMapping(): void {
    if (!this.selectedArticleNumberMapping) {
      return;
    }
    this.activeArticleNumberMappingList.entries.splice(this.activeArticleNumberMappingList.entries.indexOf(this.selectedArticleNumberMapping), 1);
    this.update.emit();
  }

  public deleteAllArticleNumberMappings(): void {
    this.activeArticleNumberMappingList.entries = [];
    this.update.emit();
  }

  public exportArticleNumberMappings(): void {
    FileUtils.saveTextFile(this.FILE_NAME, this.generateContent(), '.csv', false);
  }

  public importArticleNumberMappings($event): void {
    const input = $event.target;
    if (!input.value) {
      return;
    }
    const reader = new FileReader();
    reader.readAsText(input.files[0]);
    reader.onload = (data) => {
      const csvLines: string[] = (reader.result as string).split(this.LINES_SEPARATOR);
      if (!ArrayUtils.isEmpty(csvLines)) {
        const articleNumberMappings: ArticleNumberMappingVO[] = this.getArticleNumberMappings(csvLines);

        this.articleDataService
          .getNonExistingArticlesByNumbersAsObservable(articleNumberMappings.map((value) => value.articleNumber))
          .subscribe((nonExistingArticlesNumbers: string[]) => {
            const duplicateMappings: ArticleNumberMappingVO[] = this.getDuplicateArticleMappings(articleNumberMappings);
            const invalidMappings: ArticleNumberMappingVO[] = this.getInvalidArticleMappings(nonExistingArticlesNumbers, articleNumberMappings);
            const validMappings = this.getValidArticleMappings(articleNumberMappings, duplicateMappings, invalidMappings);
            const validAndUniqueMappings = this.getValidAndUniqueMappings(validMappings, duplicateMappings);
            this.activeArticleNumberMappingList.entries = this.activeArticleNumberMappingList.entries.concat(validAndUniqueMappings);
            this.update.emit();
            if (duplicateMappings.length > 0) {
              this.openSkipReplaceDialog(duplicateMappings);
            }
            if (invalidMappings.length > 0) {
              this.openInvalidMappingsDialog(invalidMappings);
            }
          });
      }
      input.value = '';
    };
  }

  private getArticleNumberMappings(csvLines: string[]) {
    const parsedMappings = csvLines
      .map((csvLine) => {
        const mappingValues: string[] = csvLine.split(this.VALUES_SEPARATOR);
        if (mappingValues.length >= 2) {
          const newMapping: ArticleNumberMappingVO = new ArticleNumberMappingVO();
          newMapping.articleNumber = mappingValues[0].trim();
          newMapping.customArticleNumber = mappingValues[1].trim();
          return newMapping;
        }
      })
      .filter((value) => value);
    return this.getFilteredFromDuplicatesMappings(parsedMappings);
  }

  private getFilteredFromDuplicatesMappings(parsedMappings: ArticleNumberMappingVO[]): ArticleNumberMappingVO[] {
    const parsedUniqueMappings: ArticleNumberMappingVO[] = [];
    parsedMappings.forEach((parsedMapping) => {
      if (parsedUniqueMappings.filter((parsedUniqueMapping) => parsedUniqueMapping.equals(parsedMapping)).length === 0) {
        parsedUniqueMappings.push(parsedMapping);
      }
    });
    return parsedUniqueMappings;
  }

  private openInvalidMappingsDialog(invalidNumberMappings: ArticleNumberMappingVO[]): void {
    this.articleComponentsPresenter.openInvalidArticleNumberMappingImportRecordsPopup(invalidNumberMappings);
  }

  private openSkipReplaceDialog(duplicateNumberMappings: ArticleNumberMappingVO[]): void {
    this.alertHelper
      .showAlert(
        'editCustomArticleNumberPopup.title.duplicate',
        'editCustomArticleNumberPopup.message.replace_dialog',
        null,
        [AlertButton.YES, AlertButton.NO],
        ``,
        {
          yesLabel: `editCustomArticleNumberPopup.title.replace`,
          noLabel: `editCustomArticleNumberPopup.title.skip`,
        }
      )
      .then((answer) => {
        if (answer === AlertButton.YES) {
          for (let i: number = 0; i < this.activeArticleNumberMappingList.entries.length; i++) {
            for (let j: number = 0; j < duplicateNumberMappings.length; j++) {
              if (this.isDuplicateNumberMappingVO(this.activeArticleNumberMappingList.entries[i], duplicateNumberMappings[j])) {
                this.activeArticleNumberMappingList.entries[i] = duplicateNumberMappings[j];
                duplicateNumberMappings.splice(j, 1);
                break;
              }
            }
          }
        }
      });
  }

  private getValidAndUniqueMappings(validMappings: ArticleNumberMappingVO[], duplicateMappings: ArticleNumberMappingVO[]): ArticleNumberMappingVO[] {
    const validAndUniqueMappings: ArticleNumberMappingVO[] = [];
    validMappings.forEach((numberMapping) => {
      if (this.isMappingWithArticleNumberAlreadyAdded(validAndUniqueMappings, numberMapping)) {
        duplicateMappings.push(numberMapping);
      } else {
        validAndUniqueMappings.push(numberMapping);
      }
    });
    return validAndUniqueMappings;
  }

  private isMappingWithArticleNumberAlreadyAdded(articleNumberMappings: ArticleNumberMappingVO[], numberMapping) {
    return articleNumberMappings.filter((validAndUniqueMapping) => validAndUniqueMapping.articleNumber === numberMapping.articleNumber).length > 0;
  }

  private getValidArticleMappings(
    articleMappings: ArticleNumberMappingVO[],
    duplicateMappings: ArticleNumberMappingVO[],
    invalidMappings: ArticleNumberMappingVO[]
  ): ArticleNumberMappingVO[] {
    const duplicateArticleNumberMappings: string[] = duplicateMappings.map((duplicateMapping) => duplicateMapping.articleNumber);
    const invalidArticleNumberMappings: string[] = invalidMappings.map((invalidMapping) => invalidMapping.articleNumber);
    return articleMappings.filter((articleMapping) =>
      this.isValidAndUniqueMapping(articleMapping, duplicateArticleNumberMappings, invalidArticleNumberMappings)
    );
  }

  private isValidAndUniqueMapping(
    articleMapping: ArticleNumberMappingVO,
    duplicateArticleNumberMappings: string[],
    invalidArticleNumberMappings: string[]
  ) {
    return !(
      duplicateArticleNumberMappings.includes(articleMapping.articleNumber.trim()) ||
      invalidArticleNumberMappings.includes(articleMapping.articleNumber.trim())
    );
  }

  private getDuplicateArticleMappings(articleMappings: ArticleNumberMappingVO[]): ArticleNumberMappingVO[] {
    return articleMappings.filter((articleMapping) => this.isDuplicateMappingInDataProvider(articleMapping));
  }

  private getInvalidArticleMappings(nonExistingArticlesNumbers: string[], articleMappings: ArticleNumberMappingVO[]): ArticleNumberMappingVO[] {
    return articleMappings.filter((articleMapping) => this.isArticleNumberMappingIsInvalid(articleMapping, nonExistingArticlesNumbers));
  }

  private isArticleNumberMappingIsInvalid(articleNumberMapping: ArticleNumberMappingVO, nonExistingArticlesNumbers: string[]): boolean {
    return (
      nonExistingArticlesNumbers.includes(articleNumberMapping.articleNumber) ||
      articleNumberMapping.customArticleNumber.length > this.MAX_ARTICLE_NUMBER_LENGTH
    );
  }

  private isDuplicateMappingInDataProvider(articleNumberMapping: ArticleNumberMappingVO): boolean {
    for (const articleNumber of this.activeArticleNumberMappingList.entries) {
      if (this.isDuplicateNumberMappingVO(articleNumberMapping, articleNumber)) {
        return true;
      }
    }
    return false;
  }

  private isDuplicateNumberMappingVO(first: ArticleNumberMappingVO, second: ArticleNumberMappingVO): boolean {
    return first.articleNumber.trim() === second.articleNumber.trim();
  }

  public addToFavoritesButtonClickHandler(): void {
    if (!this.projectModel) {
      return;
    }
    this._requestedFavoriteArticleNumbers = [];
    const articleIdsToAdd: number[] = [];
    const observables: Observable<any>[] = [];
    this.activeArticleNumberMappingList.entries.forEach((articleNumberMapping: ArticleNumberMappingVO) => {
      const articleNumber: string = articleNumberMapping.articleNumber;
      const article: ArticleVO = this.articleManager.getCachedArticleByArticleNo(articleNumber);
      if (!article) {
        this._requestedFavoriteArticleNumbers.push(articleNumber);
        observables.push(
          this.articleDataService.loadArticleByArticleNumber(articleNumber).pipe(
            map((articleVO: ArticleVO) => {
              if (articleVO && this.userModel) {
                this.articleByNumberLoaded(articleVO);
              } else {
                this.showFavoriteArticlesFailedAddedMessage(articleNumber);
              }
            }),
            catchError((error) => {
              this.showFavoriteArticlesFailedAddedMessage(articleNumber);
              return of(articleNumber);
            })
          )
        );
      } else {
        articleIdsToAdd.push(article.identity);
        if (ArrayUtils.isEmpty(observables)) {
          observables.push(of(null));
        }
      }
    });
    UserProfileUtils.addFavoriteArticlesToProfile(this.userModel.userProfile, articleIdsToAdd);
    forkJoin(observables).subscribe(
      () => {
        this.collectionService.notifyUpdateFavoriteArticles();
      },
      (error) => {
        this.showFavoriteArticlesFailedAddedMessage(error);
      }
    );
    if (ArrayUtils.isEmpty(this._requestedFavoriteArticleNumbers)) {
      this.showAllFavoriteArticlesSuccessfullyAddedMessage();
    }
  }

  public articleByNumberLoaded(article: ArticleVO): void {
    if (article && this.userModel) {
      UserProfileUtils.addFavoriteArticlesToProfile(this.userModel.userProfile, [article.identity]);
      if (this._requestedFavoriteArticleNumbers) {
        ArrayUtils.removeItem(article.articleNo, this._requestedFavoriteArticleNumbers);
      }
      if (ArrayUtils.isEmpty(this._requestedFavoriteArticleNumbers)) {
        this.showAllFavoriteArticlesSuccessfullyAddedMessage();
      }
    }
  }

  private showFavoriteArticlesFailedAddedMessage(articleNo: string): void {
    this.alertHelper.showError('editCustomArticleNumberPopup.error.title', 'editCustomArticleNumberPopup.error.invalid_article_number', {
      translationParams: [articleNo],
    });
  }

  private showAllFavoriteArticlesSuccessfullyAddedMessage(): void {
    this.alertHelper.showAlert(
      'editArticleNumberMappingPopup.title.all_favorite_articles_successfully_added',
      'editArticleNumberMappingPopup.message.all_favorite_articles_successfully_added',
      null,
      [AlertButton.OK],
      "info"
    );
  }

  private cloneArticleNumberMappingLists(mappingLists: ArticleNumberMappingListVO[]): ArticleNumberMappingListVO[] {
    if (ArrayUtils.isEmpty(mappingLists)) {
      return [this.createDefaultArticleNumberMappingList()];
    }
    const clonedMappingLists: ArticleNumberMappingListVO[] = [];
    for (const mappingList of mappingLists) {
      const clonedList = new ArticleNumberMappingListVO();
      clonedList.name = mappingList.name;
      clonedList.isDefault = mappingList.isDefault;
      clonedList.isActive = mappingList.isActive;
      clonedList.entries = this.cloneArticleNumberMapping(mappingList.entries);
      clonedMappingLists.push(clonedList);
    }
    return clonedMappingLists;
  }

  private createDefaultArticleNumberMappingList(): ArticleNumberMappingListVO {
    const defaultMappingList: ArticleNumberMappingListVO = new ArticleNumberMappingListVO();
    defaultMappingList.name = this.ARTICLE_NUMBER_MAPPING_LIST_DEFAULT_NAME;
    defaultMappingList.isDefault = true;
    defaultMappingList.isActive = true;
    defaultMappingList.entries = [];
    return defaultMappingList;
  }

  private cloneArticleNumberMapping(numberMappings: ArticleNumberMappingVO[]): ArticleNumberMappingVO[] {
    if (!numberMappings) {
      return [];
    }
    const clonedMappings: ArticleNumberMappingVO[] = [];
    for (const mapping of numberMappings) {
      const articleNumberMappingVO: ArticleNumberMappingVO = new ArticleNumberMappingVO();
      articleNumberMappingVO.articleNumber = mapping.articleNumber;
      articleNumberMappingVO.customArticleNumber = mapping.customArticleNumber;
      clonedMappings.push(articleNumberMappingVO);
    }
    return clonedMappings;
  }

  public saveButton_clickHandler(): void {
    this.userModel.articleNumberMappingLists = this.articleNumberMappingLists;
    this.saveArticleNumberMappingLists();
    this.close();
  }

  private saveArticleNumberMappingLists(): void {
    if (
      this.userModel.userProfile &&
      this.userModel.userProfile.isRegisteredUserAccount() &&
      this.userModel.user &&
      this.userModel.user.hasPermission("SAVE_LIST_OF_ARTICLE_NUMBER_MAPPINGS")
    ) {
      this.articleNumberMappingService.saveUserMappingLists(this.userModel.user.id, this.articleNumberMappingLists);
    }
    this.userModelService.notifyArticleNumberMappingsChanged();
  }

  public cancelButton_clickHandler(): void {
    this.close();
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  private generateContent(): string {
    return CSVUtils.createContentString(this.activeArticleNumberMappingList.entries, ['articleNumber', 'customArticleNumber']);
  }

  public getNotFavoriteArticleCollections(): ArticleCollectionVO[] {
    return this.userModel.userProfile.articleCollections.filter((c) => c.type !== ArticleCollectionVO.TYPE_FAVORITES);
  }

  public async addToLibraryButtonClickHandler(): Promise<void> {
    const observables: Observable<ArticleVO>[] = [];
    this.activeArticleNumberMappingList.entries.forEach((articleNumberMapping: ArticleNumberMappingVO) => {
      const articleNumber: string = articleNumberMapping.articleNumber;
      const article: ArticleVO = this.articleManager.getCachedArticleByArticleNo(articleNumber);
      if (!article) {
        observables.push(
          this.articleDataService.loadArticleByArticleNumber(articleNumber).pipe(
            catchError((error) => {
              this.showFavoriteArticlesFailedAddedMessage(articleNumber);
              return of(null);
            })
          )
        );
      } else {
        observables.push(of(article));
      }
    });
    const articles: ArticleVO[] = (await lastValueFrom(forkJoin(observables))).filter((a) => !!a);
    if (this.editedCollection) {
      this.addArticlesToEditedCollection(articles);
    } else {
      const articleCollection =
        await this._userComponentPresenter.presentAddMultipleArticlesToArticleCollectionPopup(articles,
          this.getNotFavoriteArticleCollections());
      this.addArticlesToCollectionAddSuccess(articleCollection);
    }
  }

  private addArticlesToEditedCollection(articles: ArticleVO[]): void {
    let articleIds: number[] = this.editedCollection.articleIds;
    const articleIdsToAdd: number[] = articles.map((a) => a.identity);
    if (ArrayUtils.isEmpty(articleIds)) {
      articleIds = articleIdsToAdd;
    } else {
      ArrayUtils.mergeArrays(articleIds, articleIdsToAdd);
    }
    this.editedCollection.articleIds = articleIds;
    this.addArticlesToCollectionAddSuccess(this.editedCollection);
  }

  private addArticlesToCollectionAddSuccess(collection: ArticleCollectionVO): void {
    const collectionId: number = collection && collection.id ? collection.id : null;
    this.collectionService.notifySingleCollectionChanged(collectionId);
  }

  public openHELPLink(): void {
    window.open(this.localeResourceManager.getValueByKey(`helplink.ownarticlenumbers`), `_blank`);
  }
}
